/**
 * sentiment 値 (-1〜+1) → HSL 色変換ヘルパー (Sprint AW、旧 InsightList.tsx L75-87 踏襲)。
 *
 * 旧 v1 ECW InsightList での挙動を等価移植:
 *   - sentiment = +1 (ポジティブ) → hue=0 (赤側) ではなく、旧コードでは
 *     `normalized = (1 - sentiment) / 2` を経由しているため、+1 → normalized=0 → hue=0 (赤)、
 *     -1 → normalized=1 → hue=240 (青)。
 *   - 注: ユーザー仕様書に「0=赤 → 240=青」と記載があり、旧の挙動と一致する。
 *     ただし「赤=ポジ / 青=ネガ」という色対応は旧の歴史的経緯 (warm=happy, cool=cool-headed)
 *     を踏襲する。逆転させたい場合は別途デザイン議論。
 *
 * `sentimentToHue` は -1/+1 範囲外の値も clamp する (LLM の outlier 出力対策)。
 */

export const SENTIMENT_HUE_MIN = 0; // 赤 (ポジティブ側、+1)
export const SENTIMENT_HUE_MAX = 240; // 青 (ネガティブ側、-1)

/** -1〜+1 範囲外を clamp。 */
export function clampSentiment(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < -1) return -1;
  if (value > 1) return 1;
  return value;
}

/**
 * sentiment (-1〜+1) → HSL hue (0〜240) に線形マップ。
 *
 * - sentiment=+1 (最ポジ) → hue=0 (赤)
 * - sentiment= 0 (中立)   → hue=120 (黄緑〜緑)
 * - sentiment=-1 (最ネガ) → hue=240 (青)
 * - 範囲外は clamp。
 */
export function sentimentToHue(value: number): number {
  const v = clampSentiment(value);
  const normalized = (1 - v) / 2; // +1→0, 0→0.5, -1→1
  return normalized * (SENTIMENT_HUE_MAX - SENTIMENT_HUE_MIN) + SENTIMENT_HUE_MIN;
}

export interface SentimentHslOpts {
  /** 彩度 (0-100、default 70)。 */
  saturation?: number;
  /** 明度 (0-100、default 50)。 */
  lightness?: number;
  /** alpha (0-1)、指定時は `hsla()` を返す。 */
  alpha?: number;
}

/**
 * sentiment (-1〜+1) を CSS `hsl(...)` 文字列に変換。
 * 旧 InsightList.tsx L80 の `hsl(${hue}, 70%, 50%)` をデフォルトとし、opts で淡色化に対応 (背景 gradient 用)。
 */
export function sentimentToHsl(value: number, opts: SentimentHslOpts = {}): string {
  const hue = sentimentToHue(value);
  const sat = opts.saturation ?? 70;
  const light = opts.lightness ?? 50;
  if (typeof opts.alpha === 'number') {
    return `hsla(${hue}, ${sat}%, ${light}%, ${opts.alpha})`;
  }
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

/**
 * card 背景用の淡色グラデーション (旧 L83-87 `getCardBgGradient` 踏襲)。
 */
export function sentimentToBgGradient(value: number): string {
  const hue = sentimentToHue(value);
  return `linear-gradient(135deg, hsl(${hue}, 60%, 96%), hsl(${hue}, 40%, 92%))`;
}
